// ============================================================
// FEATURE: DeadChannel — a haunted retro CRT TELEVISION set.
// Derived from Ambient/VhsOverlay (scanlines/flicker/tracking) +
// CrtTerminal (curved phosphor screen shell).
//
// A channel knob cycles a tiny playlist:
//   1) snow / static  2-3) artportfolio feeds behind scanlines
//   4) SMPTE-style NO SIGNAL color bars  5) a slow text crawl.
// A power button toggles a CRT power-off white-dot collapse.
//
// TACTILE CONTROLS: every chunky skeuomorphic control answers back
// with tvTactile() from _shared/feedback.js — a feature-guarded
// navigator.vibrate buzz PLUS a short synthesized audio cue (a
// channel-knob DETENT + STATIC pop, a heavy power THUNK, fine knob
// TICKS, a degauss HUM, OSD beeps, a scan sweep, antenna crackle, and
// the eerie lost-channel LOCK). The audio half honors the global mute
// via the threaded `sound` prop; the haptic half may still fire muted.
//
// CONTROLS: channel surf + a VOLUME knob, a TUNING dial, a VERTICAL-HOLD
// knob (rolls the picture), BRIGHTNESS + CONTRAST dials, a DEGAUSS button
// (color-warp wobble), and — added in this iteration:
//   • a draggable RABBIT-EAR ANTENNA whose angle sets RECEPTION (more/less
//     snow + ghosting across every feed),
//   • an on-screen CHANNEL GUIDE / OSD menu (open, move the cursor, tune),
//   • SCAN auto-tune that sweeps channels and can LOCK a hidden LOST CHANNEL
//     easter egg when reception is strong,
//   • a TRACKING adjust slider (a jitter/skew distortion band that you null
//     out toward center),
//   • three channel-MEMORY presets (store with SET, recall with a tap).
// Each new control wires the existing tvTactile() feedback + a visible effect.
//
// PERF: the static "snow" is a CHEAP CSS keyframe (animated
// background-position over a small tiled noise gradient) — NO
// per-pixel canvas loop. Only the ACTIVE channel renders. All
// animation is paused (animation-play-state) when the set is
// powered off OR scrolled off-screen / tab hidden, gated by the
// shared whenVisible() observer. The SCAN sweep is a single short
// self-clearing timeout chain (no rAF, no interval left running).
// The picture adjustments (brightness/contrast/roll/hold/signal/
// tracking) are plain inline CSS custom properties + GPU transforms.
//
// Non-draggable content component — mount as the artComp of a
// WindowsCard to inherit the draggable titlebar + minimize.
// ============================================================

import * as React from "react";
import './deadchannel.css';
import data from "./channels.json";

// Reuse ONLY existing assets. ../../../images from this file's depth.
// De-duplicated from the MAIN board: waitingRoom lives on the main surface, so
// this feed pulls deepHeavy (not on the main board) instead. skeyeSkape is an
// eye/theme piece and may recur by design. -web variant dodges oversized texture.
import skeyeSkape from "../../../images/artportfolio/skeyeSkape-web.png";
import deepHeavy from "../../../images/artportfolio/deepHeavy-web.png";
// Photoreal eyes (friendly + uncanny) for the new AMBULA-VISION channels.
import friendlyEye from "../../../images/friendlyeye1.png";
import theEye from "../../../images/theeye.png";
import eyeIris from "../../../images/eyeiris.png";
import waitingRoom from "../../../images/artportfolio/waitingRoom-web.png";
import holeHandDream from "../../../images/artportfolio/holeHandDream-web.png";

import { whenVisible, prefersReducedMotion } from '../_shared/sketchFrame';
import { tvTactile } from '../_shared/feedback';

// Map the json image keys to the statically-imported (bundled) URLs.
const IMAGE_MAP = {
    skeyeSkape: skeyeSkape,
    deepHeavy: deepHeavy,
    friendlyEye: friendlyEye,
    theEye: theEye,
    eyeIris: eyeIris,
    waitingRoom: waitingRoom,
    holeHandDream: holeHandDream,
};

// How long (seconds) each WATCHER caption holds before the next fades in. The
// full cycle is this * (number of lines); the per-line CSS delay staggers them.
const WATCHER_CAP_SECONDS = 4.5;

const CHANNELS = data.channels;
const CRAWL_TEXT = data.crawl.join("   ◆◆◆   ");

// The hidden LOST CHANNEL — not in the normal CHANNELS rotation; only reachable
// via the easter-egg paths (SCAN with strong reception, or the GUIDE's ghost row).
const LOST = data.lost;
const LOST_INDEX = -1; // sentinel state.index for "we are on the lost channel"

// SMPTE-ish bar palette (top row), reused by the css gradient via inline style.
const SMPTE_BARS = [
    "#c0c0c0", "#c0c000", "#00c0c0", "#00c000",
    "#c000c0", "#c00000", "#0000c0",
];

const PRESET_COUNT = 3; // P1..P3 channel-memory slots

// Clamp helper for the analog dials.
function clamp(v, lo, hi) {
    return v < lo ? lo : v > hi ? hi : v;
}

class DeadChannel extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            index: 0,         // current channel index into CHANNELS (or LOST_INDEX)
            on: true,         // power state
            powering: false,  // true mid power-off collapse animation
            paused: false,    // gated off-screen / tab hidden (animation-play-state)

            // --- analog picture controls (0..100 unless noted) ---
            volume: 45,       // VOLUME knob — drives the on-screen level bar
            brightness: 100,  // BRIGHTNESS dial — CSS brightness() %
            contrast: 100,    // CONTRAST dial — CSS contrast() %
            vhold: 50,        // VERTICAL HOLD — 50 = locked; off-center = rolling
            degaussing: false,// true during the brief degauss color-warp wobble

            // --- iteration: new interactive features ---
            signal: 62,       // RECEPTION 0..100 set by the draggable antenna
            antennaAngle: -28,// rabbit-ear angle in deg (visual; maps to signal)
            tracking: 50,     // TRACKING adjust — 50 = nulled; off-center = jitter
            guideOpen: false, // CHANNEL GUIDE / OSD menu visible
            guideCursor: 0,   // highlighted row in the guide
            scanning: false,  // SCAN auto-tune sweep in progress
            presets: [null, null, null], // channel-MEMORY slots (store an index)
            armPreset: false, // next preset tap STORES instead of recalls
            foundLost: false, // has the lost channel ever been discovered
        };

        // Root element for the visibility gate.
        this.rootRef = React.createRef();
        this.antennaRef = React.createRef();
        this._cleanupFrame = null;
        this._powerTimer = null;   // handle for the power-off collapse timeout
        this._degaussTimer = null; // handle for the degauss wobble timeout
        this._scanTimer = null;    // handle for the auto-tune sweep step
        this._dragMove = null;     // bound window pointermove during antenna drag
        this._dragUp = null;       // bound window pointerup during antenna drag

        this.channelUp = this.channelUp.bind(this);
        this.channelDown = this.channelDown.bind(this);
        this.togglePower = this.togglePower.bind(this);
        this.onVolume = this.onVolume.bind(this);
        this.onBrightness = this.onBrightness.bind(this);
        this.onContrast = this.onContrast.bind(this);
        this.onVhold = this.onVhold.bind(this);
        this.onTune = this.onTune.bind(this);
        this.degauss = this.degauss.bind(this);

        this.onTracking = this.onTracking.bind(this);
        this.toggleGuide = this.toggleGuide.bind(this);
        this.onGuideKey = this.onGuideKey.bind(this);
        this.startScan = this.startScan.bind(this);
        this.onAntennaDown = this.onAntennaDown.bind(this);
        this.armPreset = this.armPreset.bind(this);

        this._reduced = prefersReducedMotion();
    }

    componentDidMount() {
        // Pause every CSS animation when the TV scrolls off-screen OR the tab is
        // hidden; resume when it returns AND the tab is visible. No rAF loop here
        // — we only flip a boolean that drives animation-play-state on the CSS.
        this._cleanupFrame = whenVisible(
            this.rootRef.current,
            () => this.setState({ paused: false }),
            () => this.setState({ paused: true })
        );
    }

    componentWillUnmount() {
        if (this._cleanupFrame) {
            this._cleanupFrame();
            this._cleanupFrame = null;
        }
        // Cancel any in-flight timeouts so they never fire setState() on an
        // unmounted component.
        if (this._powerTimer != null) {
            window.clearTimeout(this._powerTimer);
            this._powerTimer = null;
        }
        if (this._degaussTimer != null) {
            window.clearTimeout(this._degaussTimer);
            this._degaussTimer = null;
        }
        if (this._scanTimer != null) {
            window.clearTimeout(this._scanTimer);
            this._scanTimer = null;
        }
        this.detachAntennaDrag();
    }

    // Thread the global mute into every tactile call. Annex passes sound={sound}
    // (soundEnabled); default true so a standalone mount still gets audio.
    fire(kind) {
        tvTactile({ kind, sound: this.props.sound !== false });
    }

    // True when we are currently parked on the hidden lost channel.
    onLost() {
        return this.state.index === LOST_INDEX;
    }

    channelUp() {
        if (!this.state.on) return;
        this.fire("channel"); // detent click + a burst of static on the switch
        // Stepping off the lost channel returns you to the normal rotation start.
        this.setState((s) => ({
            index: s.index === LOST_INDEX ? 0 : (s.index + 1) % CHANNELS.length,
        }));
    }

    channelDown() {
        if (!this.state.on) return;
        this.fire("channel");
        this.setState((s) => ({
            index: s.index === LOST_INDEX
                ? CHANNELS.length - 1
                : (s.index - 1 + CHANNELS.length) % CHANNELS.length,
        }));
    }

    togglePower() {
        // Any prior power-off collapse is now stale (rapid toggling) — drop it
        // so a queued timeout can't force the set back off after we turn it on.
        if (this._powerTimer != null) {
            window.clearTimeout(this._powerTimer);
            this._powerTimer = null;
        }
        this.stopScan(); // a sweep must not survive a power cycle

        this.fire("power"); // heavy mechanical THUNK on the way in or out

        if (this.state.on) {
            // Powering OFF: play the white-dot collapse, then go dark. Also close
            // the guide so a re-power starts clean.
            // Reduced-motion users skip straight to off (no collapse flash).
            if (this._reduced) {
                this.setState({ on: false, powering: false, guideOpen: false });
                return;
            }
            this.setState({ powering: true, guideOpen: false });
            // The collapse is a pure CSS animation; we just flip state after it.
            // A single short timeout (not a loop) sequences off — no rAF/interval.
            this._powerTimer = window.setTimeout(() => {
                this._powerTimer = null;
                this.setState({ on: false, powering: false });
            }, 420);
        } else {
            this.setState({ on: true, powering: false });
        }
    }

    // --- analog knobs/sliders ---------------------------------------------
    // Each range input fires a fine knob TICK only when its integer value
    // actually steps, so dragging produces a satisfying run of detents rather
    // than a tone per pixel. The visible effect is applied via state.

    onVolume(e) {
        const next = clamp(parseInt(e.target.value, 10) || 0, 0, 100);
        if (next !== this.state.volume) this.fire("knobTick");
        this.setState({ volume: next });
    }

    onBrightness(e) {
        const next = clamp(parseInt(e.target.value, 10) || 0, 40, 160);
        if (next !== this.state.brightness) this.fire("knobTick");
        this.setState({ brightness: next });
    }

    onContrast(e) {
        const next = clamp(parseInt(e.target.value, 10) || 0, 40, 180);
        if (next !== this.state.contrast) this.fire("knobTick");
        this.setState({ contrast: next });
    }

    onVhold(e) {
        const next = clamp(parseInt(e.target.value, 10) || 0, 0, 100);
        if (next !== this.state.vhold) this.fire("knobTick");
        this.setState({ vhold: next });
    }

    // TRACKING adjust — like a VCR's tracking knob. Off-center adds a jittering
    // distortion band + slight skew to the picture; centered (50) nulls it out.
    onTracking(e) {
        const next = clamp(parseInt(e.target.value, 10) || 0, 0, 100);
        if (next !== this.state.tracking) this.fire("knobTick");
        this.setState({ tracking: next });
    }

    // TUNING dial — purely tactile: it doesn't change the channel, it just
    // "fine tunes", firing a static-laced detent so the set feels alive.
    onTune() {
        if (!this.state.on) return;
        this.fire("channel");
    }

    degauss() {
        if (!this.state.on || this.state.degaussing) return;
        this.fire("degauss"); // low magnetic hum
        if (this._reduced) return; // skip the visual wobble for reduced-motion
        this.setState({ degaussing: true });
        if (this._degaussTimer != null) window.clearTimeout(this._degaussTimer);
        this._degaussTimer = window.setTimeout(() => {
            this._degaussTimer = null;
            this.setState({ degaussing: false });
        }, 900);
    }

    // --- CHANNEL GUIDE / OSD menu -----------------------------------------
    toggleGuide() {
        if (!this.state.on) return;
        this.fire("uiBeep");
        this.setState((s) => ({
            guideOpen: !s.guideOpen,
            // park the cursor on the current channel when opening
            guideCursor: !s.guideOpen
                ? (s.index === LOST_INDEX ? 0 : s.index)
                : s.guideCursor,
        }));
    }

    // Build the list of rows the guide shows. The hidden lost-channel row only
    // appears once reception is strong enough — that's a discovery hint.
    guideRows() {
        const rows = CHANNELS.map((c, i) => ({ ch: c.ch, label: c.label, index: i }));
        if (this.state.signal >= 85 || this.state.foundLost) {
            rows.push({ ch: LOST.ch, label: LOST.label, index: LOST_INDEX, ghost: true });
        }
        return rows;
    }

    // Arrow-key navigation + Enter-to-tune while the guide is focused.
    onGuideKey(e) {
        if (!this.state.guideOpen) return;
        const rows = this.guideRows();
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            this.fire("uiBeep");
            this.setState((s) => {
                const pos = rows.findIndex((r) => r.index === (
                    rows.some((rr) => rr.index === s.guideCursor) ? s.guideCursor : rows[0].index
                ));
                const from = pos < 0 ? 0 : pos;
                const delta = e.key === "ArrowDown" ? 1 : -1;
                const nextPos = (from + delta + rows.length) % rows.length;
                return { guideCursor: rows[nextPos].index };
            });
        } else if (e.key === "Enter") {
            e.preventDefault();
            this.tuneTo(this.state.guideCursor);
        } else if (e.key === "Escape") {
            e.preventDefault();
            this.fire("uiBeep");
            this.setState({ guideOpen: false });
        }
    }

    // Jump directly to a channel index (from the guide or a preset). Lands on the
    // lost channel with a LOCK cue when targetIndex is the sentinel.
    tuneTo(targetIndex) {
        if (!this.state.on) return;
        if (targetIndex === LOST_INDEX) {
            this.fire("lock");
            this.setState({ index: LOST_INDEX, guideOpen: false, foundLost: true });
            return;
        }
        this.fire("channel");
        this.setState({ index: clamp(targetIndex, 0, CHANNELS.length - 1), guideOpen: false });
    }

    // --- SCAN / auto-tune --------------------------------------------------
    stopScan() {
        if (this._scanTimer != null) {
            window.clearTimeout(this._scanTimer);
            this._scanTimer = null;
        }
        if (this.state.scanning) this.setState({ scanning: false });
    }

    startScan() {
        if (!this.state.on) return;
        if (this.state.scanning) { this.stopScan(); return; }

        this.fire("scan");
        // With strong reception the sweep can "lock" the hidden lost channel —
        // the core easter-egg path. Decide the destination up front so the sweep
        // is deterministic once started (no surprise mid-flight state reads).
        const lockLost = this.state.signal >= 78 && Math.random() < 0.6;
        const hops = this._reduced ? 2 : 5 + Math.floor(Math.random() * 4);

        this.setState({ scanning: true, guideOpen: false });

        let step = 0;
        const tick = () => {
            this._scanTimer = null;
            step += 1;
            if (step >= hops) {
                // Final landing.
                if (lockLost) {
                    this.fire("lock");
                    this.setState({ index: LOST_INDEX, scanning: false, foundLost: true });
                } else {
                    this.fire("channel");
                    this.setState((s) => ({
                        index: Math.floor(Math.random() * CHANNELS.length),
                        scanning: false,
                    }));
                }
                return;
            }
            // Intermediate hop: advance one channel with a click.
            this.fire("scan");
            this.setState((s) => ({
                index: s.index === LOST_INDEX ? 0 : (s.index + 1) % CHANNELS.length,
            }));
            this._scanTimer = window.setTimeout(tick, this._reduced ? 160 : 230);
        };
        this._scanTimer = window.setTimeout(tick, this._reduced ? 160 : 230);
    }

    // --- RABBIT-EAR ANTENNA (draggable reception) -------------------------
    // Dragging the antenna tip changes its angle; angle maps to RECEPTION. The
    // sweet spot (near vertical) gives the best signal; far to either side is
    // mostly snow + heavy ghosting. We listen on window so a fast drag that
    // leaves the small tip still tracks, and detach on pointerup.
    onAntennaDown(e) {
        if (!this.state.on) return;
        e.preventDefault();
        this.detachAntennaDrag();
        const pivot = this.antennaPivot();
        const move = (ev) => {
            const p = ev.touches ? ev.touches[0] : ev;
            this.applyAntenna(p.clientX, p.clientY, pivot);
        };
        const up = () => this.detachAntennaDrag();
        this._dragMove = move;
        this._dragUp = up;
        window.addEventListener("pointermove", move, { passive: true });
        window.addEventListener("pointerup", up);
        window.addEventListener("pointercancel", up);
        // Apply once on the initial press too.
        const p = e.touches ? e.touches[0] : e;
        this.applyAntenna(p.clientX, p.clientY, pivot);
    }

    detachAntennaDrag() {
        if (this._dragMove) {
            window.removeEventListener("pointermove", this._dragMove);
            this._dragMove = null;
        }
        if (this._dragUp) {
            window.removeEventListener("pointerup", this._dragUp);
            window.removeEventListener("pointercancel", this._dragUp);
            this._dragUp = null;
        }
    }

    antennaPivot() {
        const el = this.antennaRef.current;
        if (!el) return { x: 0, y: 0 };
        const r = el.getBoundingClientRect();
        // Pivot at the bottom-center of the antenna mount.
        return { x: r.left + r.width / 2, y: r.bottom };
    }

    applyAntenna(clientX, clientY, pivot) {
        const dx = clientX - pivot.x;
        const dy = pivot.y - clientY; // up is positive
        // Angle from vertical, in degrees, clamped to the rabbit-ear travel.
        let angle = Math.atan2(dx, Math.max(dy, 1)) * (180 / Math.PI);
        angle = clamp(angle, -70, 70);
        // Reception peaks near vertical (0deg) and falls off toward the extremes.
        const signal = Math.round(clamp(100 - (Math.abs(angle) / 70) * 100, 0, 100));
        if (signal !== this.state.signal) this.fire("reception"); // faint crackle
        this.setState({ antennaAngle: angle, signal });
    }

    // --- channel MEMORY presets -------------------------------------------
    // Tap "SET" to arm, then tap a preset slot to STORE the current channel into
    // it. Otherwise a preset tap RECALLS its stored channel. Re-tapping SET (or
    // storing) disarms.
    armPreset() {
        if (!this.state.on) return;
        this.fire("uiBeep");
        this.setState((s) => ({ armPreset: !s.armPreset }));
    }

    onPreset(slot) {
        if (!this.state.on) return;
        const { armPreset, index, presets } = this.state;
        if (armPreset) {
            // STORE the current channel (lost channel is storable too, sneakily).
            this.fire("uiBeep");
            const next = presets.slice();
            next[slot] = index;
            this.setState({ presets: next, armPreset: false });
            return;
        }
        const stored = presets[slot];
        if (stored == null) {
            // Empty slot: a soft beep, nothing to recall.
            this.fire("uiBeep");
            return;
        }
        this.tuneTo(stored);
    }

    presetLabel(stored) {
        if (stored == null) return "--";
        if (stored === LOST_INDEX) return LOST.ch;
        return CHANNELS[stored].ch;
    }

    renderActiveChannel() {
        if (this.onLost()) {
            return (
                <div className="dc-feed dc-feed-lost">
                    <div className="dc-lost-eye" aria-hidden="true" />
                    <div className="dc-lost-lines">
                        {LOST.lines.map((ln, i) => (
                            <div className="dc-lost-line" key={i} style={{ animationDelay: (i * 0.5) + "s" }}>
                                {ln}
                            </div>
                        ))}
                    </div>
                    <div className="dc-snow dc-snow-faint" />
                </div>
            );
        }

        const chan = CHANNELS[this.state.index];

        switch (chan.kind) {
            case "image": {
                const src = IMAGE_MAP[chan.image];
                return (
                    <div className="dc-feed dc-feed-image">
                        {/* GHOST: a second, offset copy of the feed — its opacity is
                            driven by reception (worse signal ⇒ stronger ghost). */}
                        <img className="dc-feed-img dc-ghost" src={src} alt="" draggable={false} />
                        <img className="dc-feed-img" src={src} alt="" draggable={false} />
                        {/* cheap CSS rolling-snow haze; its opacity tracks signal */}
                        <div className="dc-snow dc-snow-recv" />
                    </div>
                );
            }
            case "watcher": {
                const src = IMAGE_MAP[chan.image];
                const lines = chan.lines || [];
                const total = WATCHER_CAP_SECONDS * Math.max(lines.length, 1);
                return (
                    <div className="dc-feed dc-feed-watcher">
                        <img className="dc-feed-img dc-watcher-img" src={src} alt="" draggable={false} />
                        <div className="dc-watcher-vignette" aria-hidden="true" />
                        {/* a dark eyelid sweeps down + up: the eye blinks at you */}
                        <div className="dc-watcher-lid" aria-hidden="true" />
                        {/* warm, faintly too-knowing captions; one fades in at a time */}
                        <div className="dc-watcher-caps">
                            {lines.map((ln, i) => (
                                <div
                                    className="dc-watcher-cap"
                                    key={i}
                                    style={{
                                        animationDelay: (i * WATCHER_CAP_SECONDS) + "s",
                                        animationDuration: total + "s",
                                    }}
                                >
                                    {ln}
                                </div>
                            ))}
                        </div>
                        <div className="dc-snow dc-snow-recv" />
                    </div>
                );
            }
            case "ident": {
                const src = IMAGE_MAP[chan.image];
                return (
                    <div className="dc-feed dc-feed-ident">
                        <div className="dc-ident-eye-wrap">
                            <img className="dc-ident-eye" src={src} alt="" draggable={false} />
                        </div>
                        <div className="dc-ident-brand">AMBULA&middot;VISION</div>
                        <div className="dc-ident-slogan">{chan.slogan}</div>
                        <div className="dc-ident-standby">&mdash; PLEASE STAND BY &mdash;</div>
                        <div className="dc-snow dc-snow-recv" />
                    </div>
                );
            }
            case "bars": {
                const stops = SMPTE_BARS
                    .map((c, i) => {
                        const a = (i * 100) / SMPTE_BARS.length;
                        const b = ((i + 1) * 100) / SMPTE_BARS.length;
                        return `${c} ${a}%, ${c} ${b}%`;
                    })
                    .join(", ");
                return (
                    <div className="dc-feed dc-feed-bars">
                        <div
                            className="dc-bars"
                            style={{ background: `linear-gradient(90deg, ${stops})` }}
                        />
                        <div className="dc-bars-bottom" />
                        <div className="dc-nosignal">NO SIGNAL</div>
                    </div>
                );
            }
            case "crawl": {
                return (
                    <div className="dc-feed dc-feed-crawl">
                        <div className="dc-crawl-head">{"▲ EMERGENCY BROADCAST ▲"}</div>
                        <div className="dc-crawl-window">
                            <div className="dc-crawl-text">{CRAWL_TEXT}</div>
                        </div>
                    </div>
                );
            }
            case "snow":
            default:
                return (
                    <div className="dc-feed dc-feed-snow">
                        <div className="dc-snow" />
                    </div>
                );
        }
    }

    renderGuide() {
        const rows = this.guideRows();
        const cur = this.state.index;
        return (
            <div
                className="dc-guide"
                role="menu"
                tabIndex={0}
                aria-label="Channel guide"
                onKeyDown={this.onGuideKey}
                ref={(el) => { if (el) el.focus(); }}
            >
                <div className="dc-guide-head">CHANNEL GUIDE</div>
                <ul className="dc-guide-list">
                    {rows.map((r) => {
                        const sel = r.index === this.state.guideCursor;
                        const live = r.index === cur;
                        return (
                            <li
                                key={r.index}
                                className={
                                    "dc-guide-row" +
                                    (sel ? " dc-guide-sel" : "") +
                                    (live ? " dc-guide-live" : "") +
                                    (r.ghost ? " dc-guide-ghost" : "")
                                }
                                role="menuitemradio"
                                aria-checked={live}
                                onMouseEnter={() => this.setState({ guideCursor: r.index })}
                                onClick={() => this.tuneTo(r.index)}
                            >
                                <span className="dc-guide-ch">{r.ch}</span>
                                <span className="dc-guide-label">{r.label}</span>
                                {live && <span className="dc-guide-dot">●</span>}
                            </li>
                        );
                    })}
                </ul>
                <div className="dc-guide-foot">◄ ► TUNE &nbsp; ENTER SELECT &nbsp; GUIDE CLOSE</div>
            </div>
        );
    }

    render() {
        const {
            on, powering, paused, index,
            volume, brightness, contrast, vhold, degaussing,
            signal, antennaAngle, tracking, guideOpen, scanning,
            presets, armPreset, foundLost,
        } = this.state;

        const lost = index === LOST_INDEX;
        const chan = lost ? LOST : CHANNELS[index];

        // How far the VERTICAL HOLD knob is off its locked center (0..1). Off
        // center makes the picture ROLL; dead-center (50) locks it. We feed the
        // magnitude to CSS as a custom property that drives the roll speed.
        const rollMag = Math.abs(vhold - 50) / 50; // 0 locked .. 1 max roll
        const rolling = rollMag > 0.06;

        // TRACKING off-center ⇒ a jitter/skew distortion band. Magnitude 0..1.
        const trackMag = Math.abs(tracking - 50) / 50;
        const mistracking = trackMag > 0.08;

        // RECEPTION (0..100) ⇒ snow + ghost intensity. Strong signal = clean.
        // recv: 0 (clean) .. 1 (pure snow). Fed to CSS for overlay opacities.
        const recv = clamp((100 - signal) / 100, 0, 1);

        // One class flag drives every keyframe's play-state across the CSS tree.
        const rootClass =
            "dc-tv" +
            (on ? " dc-on" : " dc-off") +
            (powering ? " dc-powering" : "") +
            (paused ? " dc-paused" : "") +
            (rolling ? " dc-rolling" : "") +
            (mistracking ? " dc-mistrack" : "") +
            (scanning ? " dc-scanning" : "") +
            (lost ? " dc-lost" : "") +
            (degaussing ? " dc-degauss" : "");

        // Picture filter + roll, applied to the channel layer via inline vars.
        // Roll duration shrinks (faster roll) as the hold knob moves off center.
        const rollDur = rolling ? (0.25 + (1 - rollMag) * 2.2).toFixed(2) : "0";
        const screenStyle = {
            "--dc-bright": brightness / 100,
            "--dc-contrast": contrast / 100,
            "--dc-roll-dur": rollDur + "s",
            "--dc-recv": recv.toFixed(3),       // 0 clean .. 1 snow
            "--dc-ghost": (recv * 0.85).toFixed(3),
            "--dc-track": trackMag.toFixed(3),  // tracking distortion magnitude
            "--dc-skew": (mistracking ? (tracking - 50) / 50 * 1.4 : 0).toFixed(3) + "deg",
        };

        return (
            <div className={rootClass} ref={this.rootRef}>
                {/* draggable RABBIT-EAR ANTENNA — sets RECEPTION via its angle */}
                <div className="dc-antenna-rig" aria-hidden="false">
                    <div
                        className="dc-antenna"
                        ref={this.antennaRef}
                        style={{ "--dc-ant": antennaAngle + "deg" }}
                    >
                        <span className="dc-ear dc-ear-l" />
                        <span
                            className="dc-ear dc-ear-r"
                            onPointerDown={this.onAntennaDown}
                            role="slider"
                            tabIndex={0}
                            aria-label="Antenna — drag to adjust reception"
                            aria-valuenow={signal}
                            aria-valuemin={0}
                            aria-valuemax={100}
                            title="Drag the antenna to tune reception"
                        />
                        <span className="dc-ant-base" />
                    </div>
                    <span className="dc-signal-meter" title="Signal strength">
                        <span className="dc-signal-word">SIG</span>
                        <span className="dc-signal-bars">
                            {[0, 1, 2, 3, 4].map((i) => (
                                <span
                                    key={i}
                                    className={"dc-sig-bar" + (signal > i * 20 ? " dc-sig-on" : "")}
                                />
                            ))}
                        </span>
                    </span>
                </div>

                {/* curved glass tube */}
                <div className="dc-bezel">
                    <div className="dc-screen" style={screenStyle}>
                        {/* Render ONLY the active channel (powered on). */}
                        {on && (
                            <div className="dc-channel-layer">
                                {this.renderActiveChannel()}
                            </div>
                        )}

                        {/* overlays: scanlines + vignette + rolling tracking band */}
                        <div className="dc-scanlines" aria-hidden="true" />
                        <div className="dc-vignette" aria-hidden="true" />
                        {on && <div className="dc-tracking" aria-hidden="true" />}

                        {/* mistracking distortion band (TRACKING knob off-center) */}
                        {on && mistracking && (
                            <div className="dc-mistrack-band" aria-hidden="true" />
                        )}

                        {/* vertical-hold roll seam: a dark bar that sweeps when
                            the picture is unlocked, the classic CRT roll tear */}
                        {on && rolling && (
                            <div className="dc-rollbar" aria-hidden="true" />
                        )}

                        {/* reception snow veil over EVERY feed — opacity = --dc-recv */}
                        {on && !lost && (
                            <div className="dc-snow dc-snow-veil" aria-hidden="true" />
                        )}

                        {/* degauss color-warp wobble overlay (brief) */}
                        {on && degaussing && (
                            <div className="dc-degauss-wash" aria-hidden="true" />
                        )}

                        {/* SCAN sweep flash band racing across while auto-tuning */}
                        {on && scanning && (
                            <div className="dc-scan-band" aria-hidden="true" />
                        )}

                        {/* on-screen display (channel + label), VHS-blue */}
                        {on && !guideOpen && (
                            <div className="dc-osd">
                                <span className="dc-osd-ch">CH {chan.ch}</span>
                                <span className="dc-osd-label">{chan.label}</span>
                                {scanning && <span className="dc-osd-scan">SCAN…</span>}
                            </div>
                        )}

                        {/* on-screen VOLUME bar (appears as a level meter) */}
                        {on && !guideOpen && (
                            <div className="dc-vol-osd" aria-hidden="true">
                                <span className="dc-vol-osd-word">VOL</span>
                                <span className="dc-vol-osd-track">
                                    <span
                                        className="dc-vol-osd-fill"
                                        style={{ width: volume + "%" }}
                                    />
                                </span>
                            </div>
                        )}

                        {/* CHANNEL GUIDE / OSD menu overlay */}
                        {on && guideOpen && this.renderGuide()}

                        {/* power-off white-dot collapse + dark void when off */}
                        {(powering || !on) && (
                            <div className="dc-poweroff" aria-hidden="true">
                                <span className="dc-dot" />
                            </div>
                        )}
                    </div>
                </div>

                {/* control deck: faux wood/metal panel with knobs + buttons */}
                <div className="dc-controls">
                    <button
                        type="button"
                        className="dc-power"
                        onClick={this.togglePower}
                        aria-pressed={on}
                        title="Power"
                    >
                        <span className="dc-power-led" />
                        <span className="dc-btn-cap">PWR</span>
                    </button>

                    <div className="dc-channel-knob">
                        <button
                            type="button"
                            className="dc-ch-btn dc-ch-down"
                            onClick={this.channelDown}
                            disabled={!on}
                            title="Channel down"
                        >
                            {"▼"}
                        </button>
                        <div className="dc-ch-readout">
                            <span className="dc-ch-num">{on ? chan.ch : "--"}</span>
                            <span className="dc-ch-word">CH</span>
                        </div>
                        <button
                            type="button"
                            className="dc-ch-btn dc-ch-up"
                            onClick={this.channelUp}
                            disabled={!on}
                            title="Channel up"
                        >
                            {"▲"}
                        </button>
                    </div>

                    {/* TUNING dial — a clicky fine-tune that bursts static */}
                    <button
                        type="button"
                        className="dc-tune"
                        onClick={this.onTune}
                        disabled={!on}
                        title="Fine tuning"
                        aria-label="Fine tuning"
                    >
                        <span className="dc-tune-face">
                            <span className="dc-tune-pointer" />
                        </span>
                        <span className="dc-knob-cap">TUNE</span>
                    </button>

                    {/* GUIDE — toggles the on-screen channel menu */}
                    <button
                        type="button"
                        className={"dc-aux-btn" + (guideOpen ? " dc-aux-active" : "")}
                        onClick={this.toggleGuide}
                        disabled={!on}
                        title="Channel guide"
                        aria-pressed={guideOpen}
                    >
                        <span className="dc-aux-glyph">☰</span>
                        <span className="dc-knob-cap">GUIDE</span>
                    </button>

                    {/* SCAN — auto-tune sweep (can lock the lost channel) */}
                    <button
                        type="button"
                        className={"dc-aux-btn" + (scanning ? " dc-aux-active" : "")}
                        onClick={this.startScan}
                        disabled={!on}
                        title="Auto-tune scan"
                        aria-pressed={scanning}
                    >
                        <span className="dc-aux-glyph">⟳</span>
                        <span className="dc-knob-cap">SCAN</span>
                    </button>

                    {/* DEGAUSS button — a brief color-warp wobble + hum */}
                    <button
                        type="button"
                        className="dc-degauss-btn"
                        onClick={this.degauss}
                        disabled={!on}
                        title="Degauss"
                        aria-label="Degauss"
                    >
                        <span className="dc-degauss-glyph">◉</span>
                        <span className="dc-knob-cap">DEGAUSS</span>
                    </button>

                    <div className="dc-brand">AMBULA&middot;VISION</div>
                </div>

                {/* preset / memory row */}
                <div className="dc-presets">
                    <button
                        type="button"
                        className={"dc-set-btn" + (armPreset ? " dc-set-armed" : "")}
                        onClick={this.armPreset}
                        disabled={!on}
                        title={armPreset ? "Tap a preset to store" : "Arm preset store"}
                        aria-pressed={armPreset}
                    >
                        SET
                    </button>
                    {Array.from({ length: PRESET_COUNT }).map((_, slot) => {
                        const stored = presets[slot];
                        const live = on && stored != null &&
                            ((stored === LOST_INDEX && lost) || stored === index);
                        return (
                            <button
                                key={slot}
                                type="button"
                                className={
                                    "dc-preset" +
                                    (armPreset ? " dc-preset-arm" : "") +
                                    (live ? " dc-preset-live" : "")
                                }
                                onClick={() => this.onPreset(slot)}
                                disabled={!on}
                                title={
                                    armPreset
                                        ? "Store CH " + chan.ch + " to P" + (slot + 1)
                                        : (stored == null ? "Empty preset" : "Recall preset")
                                }
                            >
                                <span className="dc-preset-slot">P{slot + 1}</span>
                                <span className="dc-preset-val">{this.presetLabel(stored)}</span>
                            </button>
                        );
                    })}
                    {foundLost && (
                        <span className="dc-lost-badge" title="You found the lost channel">
                            δ FOUND
                        </span>
                    )}
                </div>

                {/* lower knob bank: analog sliders styled as travel knobs */}
                <div className="dc-knobs">
                    <label className="dc-knob">
                        <span className="dc-knob-label">VOL</span>
                        <input
                            type="range"
                            className="dc-slider"
                            min="0"
                            max="100"
                            value={volume}
                            onChange={this.onVolume}
                            disabled={!on}
                            aria-label="Volume"
                        />
                    </label>

                    <label className="dc-knob">
                        <span className="dc-knob-label">BRIGHT</span>
                        <input
                            type="range"
                            className="dc-slider"
                            min="40"
                            max="160"
                            value={brightness}
                            onChange={this.onBrightness}
                            disabled={!on}
                            aria-label="Brightness"
                        />
                    </label>

                    <label className="dc-knob">
                        <span className="dc-knob-label">CONTRAST</span>
                        <input
                            type="range"
                            className="dc-slider"
                            min="40"
                            max="180"
                            value={contrast}
                            onChange={this.onContrast}
                            disabled={!on}
                            aria-label="Contrast"
                        />
                    </label>

                    <label className="dc-knob">
                        <span className="dc-knob-label">V-HOLD</span>
                        <input
                            type="range"
                            className="dc-slider"
                            min="0"
                            max="100"
                            value={vhold}
                            onChange={this.onVhold}
                            disabled={!on}
                            aria-label="Vertical hold"
                        />
                    </label>

                    <label className="dc-knob">
                        <span className="dc-knob-label">TRACKING</span>
                        <input
                            type="range"
                            className="dc-slider"
                            min="0"
                            max="100"
                            value={tracking}
                            onChange={this.onTracking}
                            disabled={!on}
                            aria-label="Tracking"
                        />
                    </label>
                </div>
            </div>
        );
    }
}

export default DeadChannel;
